import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Center,
  Column,
  EmptyScreen,
  Icon,
  LineClamp,
  Text,
} from '~/commonComponents';
import { DotsMenu, MenuItem } from '~/commonComponents/DotsMenu';
import { IconButton } from '~/commonComponents/IconButton';
import { useReportIncidentItem } from '~/components/JobList/TodayJobs/ReportIncidentPopup/hooks';
import { incidentTypes } from '~/components/JobList/TodayJobs/ReportIncidentPopup/utils/incidentPopupUtil';
import { InviteButton } from '~/components/ProfessionalsHub/components/InviteButton';
import {
  getShouldEnforcePermanentJobV2,
  getShouldUsePendingReviews,
} from '~/growthbook';
import { useMediaQuery, useUserSelector } from '~/hooks';
import { CalendarTodayFilterIcon } from '~/images/emptyScreenIcons';
import {
  fireTodayShiftViewShiftClicked,
  fireDashboardReportIncidentClicked,
  fireReportIncidentNoShowClicked,
  fireReportIncidentWalkOutClicked,
} from '~/posthogs/events';
import { POSTHOG_EVENTS, trackPosthogEvent } from '~/services/posthog';
import {
  FETCH_TODAY_JOBS_ACTION,
  showConfirmReportIncidentPopup,
  showMessage,
} from '../../../actions';
import { createLoadingSelector } from '../../../api/selectors';
import CustomSpinner from '../../../commonComponents/CustomSpinner';
import { UserProfession, UserSubProfession } from '../../../enums/UserProfession';
import arrowRight from '../../../images/arrow_right.svg';
import { Colors } from '../../../themes/colors';
import { useTable } from '../hooks/useTable';
import { ReportIncidentPopup } from './ReportIncidentPopup/ReportIncidentPopup';

const tableHeaderStyle = {
  fontFamily: 'Nunito',
  fontWeight: 'bold',
  fontSize: '18px',
  color: Colors.primary_500,
  textAlign: 'left',
};

const tableDataBoldStyle = {
  fontFamily: 'Nunito',
  fontWeight: 700,
  fontSize: 18,
  color: Colors.neutral_600,
};

const tableDataNormalStyle = {
  fontFamily: 'Nunito',
  fontWeight: 400,
  fontSize: 16,
  color: Colors.neutral_500,
};

export const Table = ({ limit }) => {
  const { jobListScrollRef, onRowClick, onScroll, isLoadingNextPage, isLoadingFirstPage } =
    useTable({ type: 'today' });
  const [isSmallScreen] = useMediaQuery(['lg']);

  const jobList = useSelector((state) => state.job.todayJobs);
  const jobListToMap = limit ? jobList?.slice(0, limit) : jobList;
  const loadingSelector = createLoadingSelector([FETCH_TODAY_JOBS_ACTION]);
  const isLoading = useSelector((state) => loadingSelector(state));

  if (jobListToMap.length === 0 && !isLoading) {
    return (
      <Column paddingBottom={64} data-cy="todays-shifts-empty-state">
        <EmptyScreen
          Icon={() => <CalendarTodayFilterIcon />}
          title="No professionals coming in today!"
          description="Request or re-book a professional, and we'll help you bring top dental talent to you!"
          descriptionStyle={{
            textAlign: 'center',
          }}
        />
      </Column>
    );
  }

  if (isLoadingFirstPage) {
    return <CustomSpinner isLoading />;
  }

  return (
    <Box
      ref={jobListScrollRef}
      onScroll={onScroll}
      style={{
        overflowY: limit ? 'hidden' : 'auto',
        maxHeight: '740px',
        paddingBottom: 12,
      }}
      data-cy="todays-shifts-table-container"
    >
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
        }}
        className="active-shifts-table"
        data-cy="todays-shifts-table"
      >
        <thead
          style={{
            borderBottom: `2px solid ${Colors.neutral_divider}`,
          }}
          data-cy="todays-shifts-table-header"
        >
          <th style={{ opacity: 0, width: 60 }}>#</th>
          <th
            style={{
              ...tableHeaderStyle,
              textAlign: 'left',
              paddingLeft: 16,
            }}
            data-cy="table-header-professional"
          >
            Professional
          </th>
          {!isSmallScreen && (
            <th style={tableHeaderStyle} data-cy="table-header-office">
              Office
            </th>
          )}
          <th style={tableHeaderStyle} data-cy="table-header-shift-details">
            Details
          </th>
          <th style={{ ...tableHeaderStyle, textAlign: 'center' }} data-cy="table-header-invite">
            Rebook
          </th>
          <th style={{ ...tableHeaderStyle, textAlign: 'center' }} data-cy="table-header-report">
            Report
          </th>
          <th style={{ opacity: 0 }}>#</th>
        </thead>
        <tbody data-cy="todays-shifts-table-body">
          {jobListToMap.map((job, index) => (
            <TableItem
              job={job}
              onRowClick={onRowClick}
              isLastItem={index === jobListToMap.length - 1}
            />
          ))}
        </tbody>
      </table>

      {jobListToMap?.length === 0 && !isLoading && (
        <div>
          <p className="font_bold" style={{ textAlign: 'center' }}>
            There are no scheduled shifts for today.
          </p>
        </div>
      )}
      {isLoadingNextPage && <CustomSpinner isLoading size={32} style={{ marginTop: 20 }} />}
      <ReportIncidentPopup />
    </Box>
  );
};

export const TableItem = ({ job, onRowClick, isLastItem }) => {
  const dispatch = useDispatch();
  const {
    canReport,
    isReported,
    isShiftStartedLessThan30Minutes,
    isShiftNotStartedYet,
    onReportIncidentClick,
  } = useReportIncidentItem(job);
  const isPendingReviewsEnabled = useRef(getShouldUsePendingReviews()).current;
  const [isSmallScreen] = useMediaQuery(['lg']);
  const user = useUserSelector();

  const handleIconClick = (e) => {
    e.stopPropagation();
    if (isShiftStartedLessThan30Minutes) {
      dispatch(
        showMessage({
          title: 'Please Wait Before Reporting',
          message: (
            <>
              Reporting is unavailable for the first 30 minutes after the shift starts. If the
              professional still hasn’t arrived after that, please return to submit a report. Thank
              you for your patience.
            </>
          ),
          popupProps: {
            rightButtonText: 'Close',
            descriptionStyle: {
              marginBottom: 40,
              width: 420,
            },
          },
        }),
      );
      return;
    }
  };

  const getIconColor = (isHovered) => {
    if (isReported) {
      return Colors.warning_500;
    }

    if (isShiftNotStartedYet) {
      return Colors.neutral_200;
    }

    if (isHovered) {
      return Colors.warning_500;
    }

    return Colors.neutral_200;
  };

  const getTooltipText = () => {
    if (isReported) {
      return 'Reported';
    }
    return 'Report Incident';
  };

  return (
    <>
      <tr
        key={job.id}
        style={{
          cursor: 'pointer',
          borderBottom: `2px solid ${Colors.neutral_divider}`,
          paddingBottom: 8,
        }}
        onClick={() => {
          fireTodayShiftViewShiftClicked();
          onRowClick(job);
        }}
        data-cy="shift-table-row"
        data-job-id={job.id}
      >
        <td>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <img
              src={job.professional?.image}
              style={{
                width: 60,
                height: 60,
                objectFit: 'contain',
                borderRadius: 60,
                border: `1px solid ${Colors.neutral_100}`,
              }}
              alt=""
              data-cy="professional-avatar"
            />
          </div>
        </td>
        <td width={isPendingReviewsEnabled ? '20%' : 'auto'} style={{ paddingRight: '10px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: isPendingReviewsEnabled ? 'left' : 'center',
              marginLeft: 16,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
              }}
              data-cy="professional-info"
            >
              <span
                style={tableDataBoldStyle}
                data-cy="professional-name"
              >{`${job.professional?.full_name}`}</span>
              <span style={tableDataNormalStyle} data-cy="professional-role">
                {`${
                  UserSubProfession.getName(job.subProfession) ??
                  UserProfession.getName(job.profession)
                }`}
              </span>
            </div>
          </div>
        </td>
        {!isSmallScreen && (
          <td width={isPendingReviewsEnabled ? '30%' : 'auto'} style={{ paddingRight: 24 }}>
            {isPendingReviewsEnabled ? (
              <LineClamp
                lineClamp={2}
                text={job.offer_owner.office}
                style={{ ...tableDataNormalStyle, paddingRight: 10 }}
                data-cy="office-name"
              />
            ) : (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <span style={tableDataNormalStyle} data-cy="office-name">
                  {job.offer_owner.office}
                </span>
              </div>
            )}
          </td>
        )}
        <td width={isPendingReviewsEnabled ? '20%' : 'auto'}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: isPendingReviewsEnabled ? 'left' : 'center',
            }}
            data-cy="shift-details"
          >
            {getShouldEnforcePermanentJobV2() && (
              <Box
                alignSelf="flex-start"
                paddingBlock={2}
                paddingInline={10}
                marginBottom={5}
                borderRadius={8}
                backgroundColor={
                  job?.isOnsiteInterview
                    ? Colors.jobs_interview_background
                    : Colors.jobs_shift_background
                }
              >
                <Text color="neutral_500">{job?.isOnsiteInterview ? 'INTERVIEW' : 'SHIFT'}</Text>
              </Box>
            )}
            <span
              style={{
                ...tableDataBoldStyle,
                fontSize: 16,
                color: Colors.neutral_500,
              }}
              data-cy="shift-date"
            >
              {job.shift?.local_date}
            </span>
            <span style={tableDataNormalStyle} data-cy="shift-time">
              {`${job.shift?.local_start_time} - ${job.shift?.local_end_time}`}
            </span>
          </div>
        </td>
        <td>
          <Center>
            {!job?.isOnsiteInterview && (
              <InviteButton
                user={{
                  id: job.professional._id,
                  image: job.professional.image,
                  name: job.professional.full_name,
                  profession: job.profession,
                  professions: [job.profession],
                  officeId: job.offer_owner._id,
                }}
                position="bottom"
                source="dashboard"
                shiftId={job.id || job._id}
                CustomMenuIcon={({ isHovered }) => {
                  return (
                    <Center>
                      <IconButton
                        Icon={
                          <Icon
                            name="share"
                            color={isHovered ? Colors.secondary_300 : Colors.secondary_500}
                          />
                        }
                        tooltipContent="Rebook Me!"
                        buttonStyle={{
                          height: 48,
                          width: 48,
                        }}
                      />
                    </Center>
                  );
                }}
              />
            )}
          </Center>
        </td>
        <td>
          <Center>
            <DotsMenu
                position="bottom"
                CustomMenuIcon={({ isHovered, isDisabled }) => (
                  <IconButton
                    Icon={<Icon name="warning" color={getIconColor(isHovered)} />}
                    onClick={!canReport ? handleIconClick : () => fireDashboardReportIncidentClicked()}
                    tooltipContent={getTooltipText()}
                    hideTooltip={isShiftNotStartedYet}
                    disabled={isDisabled}
                    buttonStyle={{
                      width: 48,
                      height: 48,
                    }}
                  />
                )}
                disabled={isReported || isShiftNotStartedYet}
                dataCy="report-incident-button"
              >
                <MenuItem
                  text="Report No-Show"
                  onClick={(e) => {
                    e?.stopPropagation?.();
                    fireReportIncidentNoShowClicked();
                    onReportIncidentClick();
                    dispatch(
                      showConfirmReportIncidentPopup({ incidentType: incidentTypes.noShow }),
                    );
                  }}
                  dataCy="report-no-show-option"
                />
                <MenuItem
                  text="Report Walk-Out"
                  onClick={(e) => {
                    e?.stopPropagation?.();
                    fireReportIncidentWalkOutClicked();
                    onReportIncidentClick();
                    dispatch(
                      showConfirmReportIncidentPopup({ incidentType: incidentTypes.walkedOut }),
                    );
                  }}
                  dataCy="report-walked-out-option"
                />
              </DotsMenu>
          </Center>
        </td>
        <td>
          <div
            style={{
              display: 'flex',
              justifyContent: isPendingReviewsEnabled ? 'flex-end' : 'center',
            }}
          >
            <img src={arrowRight} style={{ width: 10, objectFit: 'cover' }} alt="arrow_right" />
          </div>
        </td>
      </tr>
    </>
  );
};
